import { Fragment, useEffect, useRef, useState, useMemo } from "react"
import { Trans, useTranslation } from "react-i18next"
import { useQuery } from "react-query"
import { ChevronDownIcon, ChevronRightIcon, XIcon } from "@heroicons/react/solid"
import { MenuIcon } from "@heroicons/react/outline"
import { Button, Loader, Notification, SearchInput } from "../../components"
import { previewPickingOrder, createPickingOrder } from "../../services"
import { PICK_TYPE_STYLES } from "../../utils/pickingStyles"
import { PickingOrderErrorsPanel } from "./AssignPickingOrders/PickingOrderErrorsPanel"

// Identifies a task uniquely across preview/confirm. Must match backend's
// (order_id, warehouse_location_id, container_id, pick_type) tuple.
const taskKey = (task) =>
    `${task.order_id}|${task.warehouse_location_id}|${task.container_id ?? "null"}|${task.pick_type}`

// Categorical colors for orders that end up split across multiple pickers, so
// the same order is visually traceable across picker tables.
const ORDER_SPLIT_COLORS = [
    "bg-purple-100 text-purple-800",
    "bg-pink-100 text-pink-800",
    "bg-indigo-100 text-indigo-800",
    "bg-emerald-100 text-emerald-800",
    "bg-green-100 text-green-800",
]

// Click-to-copy value with a hover affordance + brief "copiado" feedback.
const Copyable = ({ value, children, className = "" }) => {
    const { i18n } = useTranslation()
    const [copied, setCopied] = useState(false)
    const onCopy = (e) => {
        e.stopPropagation()
        if (value == null || value === "") return
        if (navigator.clipboard?.writeText) navigator.clipboard.writeText(String(value))
        setCopied(true)
        setTimeout(() => setCopied(false), 1200)
    }
    return (
        <span
            onClick={onCopy}
            title={i18n.t("picking_tasks.multi_picker.copy_hint")}
            className={`cursor-pointer rounded px-1 -mx-1 hover:bg-blue-50 hover:text-blue-700 ${className}`}
        >
            {children ?? value}
            {copied && <span className="ml-1 text-xs text-green-600">{i18n.t("picking_tasks.multi_picker.copied")}</span>}
        </span>
    )
}

export const MultiPickerAssignView = ({ open, orders = [], pickers = [], onBack, onSuccess }) => {
    const { i18n } = useTranslation()
    const [assignments, setAssignments] = useState({})
    const [suggestedTasks, setSuggestedTasks] = useState([])
    const [busy, setBusy] = useState(false)
    const [notification, setNotification] = useState(null)
    const [confirmErrors, setConfirmErrors] = useState([])
    // Orders that failed at confirm-time with a per-order error (422). Folded into
    // the exclusion set so a retry of "Confirmar" doesn't resend the same failing
    // orders (which would loop identically). Reset when the dialog reopens.
    const [stickyExcludedIds, setStickyExcludedIds] = useState(() => new Set())
    const [expandedRows, setExpandedRows] = useState(() => new Set())
    const [viewMode, setViewMode] = useState("picker")
    const [openPickers, setOpenPickers] = useState(() => new Set())
    const [removedPickerIds, setRemovedPickerIds] = useState(() => new Set())
    const [draggedTaskKeys, setDraggedTaskKeys] = useState(null)
    const [dragOverPickerId, setDragOverPickerId] = useState(null)
    const [query, setQuery] = useState("")
    const [pickTypeFilter, setPickTypeFilter] = useState("")

    // Pickers still in play (the coordinator can drop empty ones). The preview
    // still runs with the original picker set; removal only hides them from the
    // working views and the confirm payload.
    const activePickers = useMemo(
        () => pickers.filter(p => !removedPickerIds.has(p.id)),
        [pickers, removedPickerIds]
    )

    const toggleInSet = (setter, key) => {
        setter(prev => {
            const next = new Set(prev)
            if (next.has(key)) next.delete(key)
            else next.add(key)
            return next
        })
    }

    const toggleExpand = (key) => toggleInSet(setExpandedRows, key)

    const pickerIds = useMemo(() => pickers.map(p => p.id), [pickers])

    const { data: preview, isLoading, isError, error: previewError, refetch } = useQuery(
        ["picking_order_preview", orders, pickerIds],
        () => previewPickingOrder(orders, pickerIds),
        { enabled: open && orders.length > 0 && pickerIds.length > 0, refetchOnWindowFocus: false, retry: false }
    )

    // Seed suggestedTasks + assignments from the *first* preview response only.
    // Reset on dialog close so the next open starts fresh. This protects manual
    // edits from being overwritten if the query refetches.
    const seededRef = useRef(false)

    useEffect(() => {
        if (!open) {
            seededRef.current = false
            setExpandedRows(new Set())
            setStickyExcludedIds(new Set())
            setConfirmErrors([])
        }
    }, [open])

    useEffect(() => {
        if (!preview?.suggested_tasks) return
        if (seededRef.current) return
        setSuggestedTasks(preview.suggested_tasks)
        const initial = {}
        for (const task of preview.suggested_tasks) {
            initial[taskKey(task)] = task.suggested_picker_id
        }
        setAssignments(initial)
        seededRef.current = true
    }, [preview])

    const handleRebalance = () => {
        if (!preview?.suggested_tasks) return
        const initial = {}
        for (const task of preview.suggested_tasks) {
            // Respect removed pickers: their suggested tasks go back to pending.
            const sp = task.suggested_picker_id
            initial[taskKey(task)] = removedPickerIds.has(sp) ? null : sp
        }
        setAssignments(initial)
    }

    const handleChangePicker = (key, newPickerId) => {
        const parsed = newPickerId ? parseInt(newPickerId, 10) : null
        setAssignments(prev => ({ ...prev, [key]: parsed }))
    }

    const loads = useMemo(() => {
        const result = { unassigned: { expected_quantity: 0, tasks_count: 0 } }
        for (const picker of pickers) {
            result[picker.id] = { expected_quantity: 0, tasks_count: 0 }
        }
        for (const task of suggestedTasks) {
            const key = taskKey(task)
            const pickerId = assignments[key]
            const target = pickerId && result[pickerId] ? result[pickerId] : result.unassigned
            target.expected_quantity += (task.expected_quantity || 0)
            target.tasks_count += 1
        }
        return result
    }, [suggestedTasks, assignments, pickers])

    const applySuggestionFromMismatch = (data) => {
        if (!Array.isArray(data?.suggested_tasks)) return
        setSuggestedTasks(data.suggested_tasks)
        const next = {}
        for (const task of data.suggested_tasks) {
            next[taskKey(task)] = task.suggested_picker_id
        }
        setAssignments(next)
    }

    const handleConfirm = async () => {
        setBusy(true)
        setConfirmErrors([])
        // Set to true on paths that intentionally leave the loader on screen
        // while we wait for the parent to unmount the dialog via onSuccess.
        let keepBusy = false
        try {
            const assignmentsArray = suggestedTasks
                .filter(t => !!assignments[taskKey(t)])
                .map(t => ({
                    order_id: t.order_id,
                    warehouse_location_id: t.warehouse_location_id,
                    container_id: t.container_id,
                    pick_type: t.pick_type,
                    picker_id: assignments[taskKey(t)]
                }))

            const firstPickerId = activePickers[0]?.id

            // Don't send orders that can't be processed: the ones the preview
            // excluded (e.g. no longer PENDING) plus any that failed a previous
            // confirm attempt (stickyExcludedIds). Otherwise a retry resends the
            // same failing orders and loops. Filter both the orders list and the
            // assignments (by order_id) so they stay consistent.
            const excludedIds = new Set([
                ...(preview?.excluded || []).map(e => e.order?.id),
                ...stickyExcludedIds,
            ])
            const ordersToConfirm = orders.filter(o => !excludedIds.has(o.id))
            const assignmentsToConfirm = assignmentsArray.filter(a => !excludedIds.has(a.order_id))

            if (ordersToConfirm.length === 0) {
                setNotification({ type: "error", message: i18n.t("picking_tasks.multi_picker.confirm_error") })
                return
            }

            await createPickingOrder({
                orders: ordersToConfirm,
                assigned_to_id: firstPickerId,
                assignments: assignmentsToConfirm
            })

            setNotification({ type: "success", message: i18n.t("picking_tasks.multi_picker.confirm_success") })
            keepBusy = true
            setTimeout(() => onSuccess(), 800)
        } catch (error) {
            const code = error.response?.data?.code
            const status = error.response?.status

            if (status === 409 && code === "assignments_mismatch") {
                // Stock changed between preview and confirm — reload with fresh suggestion.
                applySuggestionFromMismatch(error.response.data)
                setNotification({
                    type: "error",
                    message: i18n.t("picking_tasks.multi_picker.stale_distribution")
                })
                return
            }

            if (status === 409 && code === "picking_order_already_exists") {
                // Race condition / retry — picking order already created. Treat as success-ish.
                setNotification({
                    type: "info",
                    message: i18n.t("picking_tasks.multi_picker.already_exists")
                })
                keepBusy = true
                setTimeout(() => onSuccess(), 800)
                return
            }

            if (status === 409 && code === "sku_or_location_split_across_pickers") {
                // Manual edits violated "same SKU/location must go to the same picker".
                // Refetch the preview so the backend hands us a valid distribution.
                seededRef.current = false
                refetch()
                setNotification({
                    type: "error",
                    message: i18n.t("picking_tasks.multi_picker.split_across_pickers")
                })
                return
            }

            const apiError = error.response?.data?.error
            // Unprocessable picking order(s): surface the per-order errors so the
            // operator can read each failing order and report it to tech.
            if (apiError?.errors?.length > 0) {
                setConfirmErrors(apiError.errors)
                // Fold these into the exclusion set so re-clicking Confirmar drops
                // them and proceeds with the still-valid orders instead of looping.
                setStickyExcludedIds(prev => {
                    const next = new Set(prev)
                    apiError.errors.forEach(e => { if (e.order?.id != null) next.add(e.order.id) })
                    return next
                })
                setNotification({
                    type: "error",
                    message: apiError.message || i18n.t("picking_tasks.multi_picker.confirm_error"),
                })
            } else {
                setNotification({ type: "error", message: typeof apiError === "string" ? apiError : i18n.t("picking_tasks.multi_picker.confirm_error") })
            }
        } finally {
            if (!keepBusy) setBusy(false)
        }
    }

    const handleCancel = () => {
        // Nothing was persisted on the backend — go back to picker selection.
        onBack()
    }

    const previewErrorMessage = useMemo(() => {
        if (!isError) return null
        const data = previewError?.response?.data
        return data?.error || i18n.t("picking_tasks.multi_picker.preview_error")
    }, [isError, previewError, i18n])

    const ordersByIdMemo = useMemo(() => {
        const map = {}
        for (const t of suggestedTasks) {
            if (t.order_id != null && !map[t.order_id]) {
                map[t.order_id] = { order_number: t.order_number, store: t.store }
            }
        }
        return map
    }, [suggestedTasks])

    const totalLoad = useMemo(() => suggestedTasks.reduce((acc, t) => acc + (t.expected_quantity || 0), 0), [suggestedTasks])
    // Orders actually being distributed = selected minus the ones the preview
    // excluded. The header piece count (totalLoad) already reflects only the valid
    // tasks, so the order count must match to avoid a contradictory sentence.
    const includedCount = Math.max(0, orders.length - (preview?.excluded?.length || 0))
    const unassignedCount = loads.unassigned?.tasks_count || 0

    const hasEdits = useMemo(() => {
        if (suggestedTasks.length === 0) return false
        return suggestedTasks.some(t => {
            const key = taskKey(t)
            return assignments[key] !== t.suggested_picker_id
        })
    }, [suggestedTasks, assignments])

    const pickerNameById = useMemo(() => {
        const m = {}
        for (const p of pickers) m[p.id] = `${p.first_name} ${p.last_name}`
        return m
    }, [pickers])

    const pickTypeLabel = (type) => i18n.t(`picking_tasks.pick_types.${type}`)

    // Per-picker distribution: tasks, pieces, and pick-type counts, plus an
    // "unassigned" bucket for any task without a picker.
    const skusOfTask = (t) => (Array.isArray(t.products) && t.products.length > 0)
        ? t.products.flatMap(p => p.skus || [])
        : (t.skus || [])

    const pickTypeOptions = useMemo(
        () => Array.from(new Set(suggestedTasks.map(t => t.pick_type))),
        [suggestedTasks]
    )

    // Display-only filter: text matches SKU / order number / location, plus an
    // optional pick-type dropdown. Assignments/confirm always operate on the full set.
    const visibleTasks = useMemo(() => {
        const q = query.trim().toLowerCase()
        return suggestedTasks.filter(t => {
            if (pickTypeFilter && t.pick_type !== pickTypeFilter) return false
            if (!q) return true
            return String(t.order_number || "").toLowerCase().includes(q)
                || String(t.location_code || "").toLowerCase().includes(q)
                || skusOfTask(t).join(" ").toLowerCase().includes(q)
        })
    }, [suggestedTasks, query, pickTypeFilter])

    const distribution = useMemo(() => {
        const makeBucket = (picker) => ({ picker, tasks: [], pieces: 0, skus: new Set(), orders: new Set() })
        const byPicker = new Map()
        for (const p of activePickers) byPicker.set(p.id, makeBucket(p))
        const unassigned = makeBucket(null)
        for (const t of visibleTasks) {
            const pid = assignments[taskKey(t)]
            const bucket = (pid && byPicker.has(pid)) ? byPicker.get(pid) : unassigned
            bucket.tasks.push(t)
            bucket.pieces += t.expected_quantity || 0
            bucket.orders.add(t.order_id)
            skusOfTask(t).forEach(s => bucket.skus.add(s))
        }
        return { byPicker, unassigned }
    }, [activePickers, visibleTasks, assignments])

    // Orders whose tasks are assigned to more than one picker get a distinct color.
    const splitOrderColorById = useMemo(() => {
        const orderPickers = new Map()
        for (const t of suggestedTasks) {
            const pid = assignments[taskKey(t)]
            if (!pid) continue
            if (!orderPickers.has(t.order_id)) orderPickers.set(t.order_id, new Set())
            orderPickers.get(t.order_id).add(pid)
        }
        const colorById = {}
        let i = 0
        for (const [orderId, pickerSet] of orderPickers) {
            if (pickerSet.size > 1) {
                colorById[orderId] = ORDER_SPLIT_COLORS[i % ORDER_SPLIT_COLORS.length]
                i++
            }
        }
        return colorById
    }, [suggestedTasks, assignments])

    const reassignTasksTo = (taskKeys, pickerId) => {
        if (!taskKeys || taskKeys.length === 0) return
        setAssignments(prev => {
            const next = { ...prev }
            taskKeys.forEach(k => { next[k] = pickerId })
            return next
        })
    }

    // Auto-assign pending tasks: prefer each task's suggested picker (if still
    // active), otherwise drop it on the least-loaded active picker to balance.
    const autoAssignPending = () => {
        if (activePickers.length === 0) return
        const load = {}
        activePickers.forEach(p => { load[p.id] = 0 })
        for (const t of suggestedTasks) {
            const pid = assignments[taskKey(t)]
            if (pid != null && load[pid] != null) load[pid] += t.expected_quantity || 0
        }
        const pending = suggestedTasks
            .filter(t => !assignments[taskKey(t)])
            .sort((a, b) => (b.expected_quantity || 0) - (a.expected_quantity || 0))
        const next = { ...assignments }
        for (const t of pending) {
            const suggested = t.suggested_picker_id
            let target = (suggested != null && load[suggested] != null)
                ? suggested
                : activePickers.reduce((min, p) => (load[p.id] < load[min.id] ? p : min), activePickers[0]).id
            next[taskKey(t)] = target
            load[target] += t.expected_quantity || 0
        }
        setAssignments(next)
    }

    // Per-order distribution: which pickers work each order and how much.
    const orderGroups = useMemo(() => {
        const map = new Map()
        for (const t of visibleTasks) {
            if (!map.has(t.order_id)) {
                map.set(t.order_id, {
                    order_id: t.order_id,
                    order_number: t.order_number,
                    store: t.store,
                    tasks: 0,
                    pieces: 0,
                    byPicker: new Map()
                })
            }
            const o = map.get(t.order_id)
            o.tasks += 1
            o.pieces += t.expected_quantity || 0
            const pid = assignments[taskKey(t)]
            const k = pid || "unassigned"
            if (!o.byPicker.has(k)) {
                o.byPicker.set(k, {
                    name: pid ? (pickerNameById[pid] || `Picker ${pid}`) : i18n.t("picking_tasks.multi_picker.unassigned"),
                    unassigned: !pid,
                    tasks: 0,
                    pieces: 0
                })
            }
            const b = o.byPicker.get(k)
            b.tasks += 1
            b.pieces += t.expected_quantity || 0
        }
        return Array.from(map.values())
    }, [visibleTasks, assignments, pickerNameById, i18n])

    return (
        <>
                    <Loader show={busy || isLoading} />
                    <div className="flex-shrink-0 pr-8">
                        <h3 className="text-2xl font-bold text-gray-800">
                            {i18n.t("picking_tasks.multi_picker.distribution_title")}
                        </h3>
                        <p className="mt-1 text-gray-500">
                            <Trans
                                i18nKey="picking_tasks.multi_picker.distribution_summary"
                                values={{
                                    users: i18n.t("picking_tasks.multi_picker.summary_users", { count: pickers.length }),
                                    pieces: i18n.t("picking_tasks.multi_picker.summary_pieces", { count: totalLoad, formatted: totalLoad.toLocaleString() }),
                                    ordersArticle: i18n.t("picking_tasks.multi_picker.summary_orders_article", { count: includedCount }),
                                    orders: i18n.t("picking_tasks.multi_picker.summary_orders", { count: includedCount })
                                }}
                                components={{ b: <span className="font-semibold text-gray-700" /> }}
                            />
                        </p>
                    </div>

                    {preview?.excluded?.length > 0 && (
                        <div className="flex-shrink-0 mt-3">
                            <PickingOrderErrorsPanel
                                tone="amber"
                                errors={preview.excluded}
                                title={i18n.t("picking_tasks.multi_picker.excluded_title", { count: preview.excluded.length })}
                            />
                        </div>
                    )}

                    {suggestedTasks.length > 0 && (
                        <div className="flex-shrink-0 mt-4 flex items-center gap-3">
                            <div className="flex-1">
                                <SearchInput
                                    onChange={(v) => setQuery(v)}
                                    debounce={200}
                                    placeholder={i18n.t("picking_tasks.multi_picker.search_placeholder")}
                                    className="focus-within:text-gray-600 text-gray-400"
                                />
                            </div>
                            <select
                                value={pickTypeFilter}
                                onChange={(e) => setPickTypeFilter(e.target.value)}
                                className="border border-gray-300 rounded pl-2 pr-8 py-2 text-sm text-gray-700 flex-shrink-0"
                            >
                                <option value="">{i18n.t("picking_tasks.multi_picker.all_pick_types")}</option>
                                {pickTypeOptions.map(pt => (
                                    <option key={pt} value={pt}>{pickTypeLabel(pt)}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {suggestedTasks.length > 0 && (
                        <div className="flex-shrink-0 mt-3 flex items-center justify-between gap-2">
                            <div className="inline-flex rounded-lg border border-gray-200 p-0.5 bg-gray-50 text-sm">
                                {[["picker", "view_by_picker"], ["task", "view_by_location"], ["order", "view_by_order"]].map(([mode, labelKey]) => (
                                    <button
                                        key={mode}
                                        type="button"
                                        onClick={() => setViewMode(mode)}
                                        className={`px-3 py-1.5 rounded-md transition-colors ${viewMode === mode ? "bg-white shadow-sm text-gray-900 font-medium" : "text-gray-500 hover:text-gray-700"}`}
                                    >
                                        {i18n.t(`picking_tasks.multi_picker.${labelKey}`)}
                                    </button>
                                ))}
                            </div>
                            {viewMode === "picker" && activePickers.length > 0 && (() => {
                                const allOpen = activePickers.every(p => openPickers.has(p.id))
                                return (
                                    <button
                                        type="button"
                                        className="text-blue-600 font-medium hover:text-blue-700 text-sm whitespace-nowrap"
                                        onClick={() => setOpenPickers(allOpen ? new Set() : new Set(activePickers.map(p => p.id)))}
                                    >
                                        {i18n.t(allOpen ? "picking_tasks.multi_picker.collapse_all" : "picking_tasks.multi_picker.expand_all")}
                                    </button>
                                )
                            })()}
                        </div>
                    )}

                    <div className="flex-1 min-h-0 overflow-y-auto -mx-6 px-6 mt-4">
                    {isError && (() => {
                        // When the backend returns structured per-order errors, let the
                        // panel explain each order and keep the banner neutral — the old
                        // heuristic body (guessing from the raw string) could contradict
                        // the panel. Only fall back to the heuristic when there is no
                        // structured errors[] to show.
                        const structuredErrors = previewError?.response?.data?.errors
                        const hasStructuredErrors = Array.isArray(structuredErrors) && structuredErrors.length > 0
                        return (
                            <div className="p-8 text-center">
                                <div className="text-base font-medium text-gray-800 mb-1">{i18n.t("picking_tasks.multi_picker.distribution_error_title")}</div>
                                <p className="text-sm text-gray-500 max-w-md mx-auto">
                                    {hasStructuredErrors
                                        ? i18n.t("picking_tasks.multi_picker.distribution_error_see_details")
                                        : (() => {
                                            const raw = JSON.stringify(previewError?.response?.data || "")
                                            if (/stock/i.test(raw)) {
                                                return i18n.t("picking_tasks.multi_picker.distribution_error_mixed_types")
                                            }
                                            // Match the specific backend signal, not a bare
                                            // "status" substring (which appears in many unrelated
                                            // error bodies and would assert a wrong cause).
                                            if (/not in pending status/i.test(raw) || /invalid_order_status/i.test(raw)) {
                                                return i18n.t("picking_tasks.multi_picker.distribution_error_not_pending")
                                            }
                                            return previewErrorMessage
                                        })()}
                                </p>
                                <button onClick={() => refetch()} className="mt-4 text-blue-600 font-medium hover:text-blue-700">
                                    {i18n.t("picking_tasks.multi_picker.retry")}
                                </button>
                                {hasStructuredErrors ? (
                                    <div className="mt-4 max-w-md mx-auto text-left">
                                        <PickingOrderErrorsPanel errors={structuredErrors} />
                                    </div>
                                ) : (
                                    <div className="mt-3 text-xs text-gray-300">{previewErrorMessage}</div>
                                )}
                            </div>
                        )
                    })()}

                    {!isLoading && !isError && suggestedTasks.length === 0 && (
                        <div className="p-6 text-center text-gray-500">
                            {i18n.t("picking_tasks.empty")}
                        </div>
                    )}

                    {suggestedTasks.length > 0 && viewMode === "task" && (
                        <>
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{i18n.t("picking_tasks.columns.store")}</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{i18n.t("picking_tasks.columns.order_number")}</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{i18n.t("picking_tasks.columns.location")}</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{i18n.t("picking_tasks.columns.sku")}</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{i18n.t("picking_tasks.columns.pick_type")}</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{i18n.t("picking_tasks.columns.quantity")}</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{i18n.t("picking_tasks.columns.picker")}</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {visibleTasks.map((task) => {
                                        const key = taskKey(task)
                                        const orderInfo = ordersByIdMemo[task.order_id] || {}
                                        const skus = task.skus || []
                                        const products = task.products || []
                                        const primarySku = skus[0] || "—"
                                        const extraSkus = skus.length > 1 ? skus.length - 1 : 0
                                        const isExpanded = expandedRows.has(key)
                                        return (
                                            <Fragment key={key}>
                                                <tr>
                                                    <td className="px-4 py-3 text-sm">{orderInfo.store?.name || task.store?.name}</td>
                                                    <td className="px-4 py-3 text-sm">#{orderInfo.order_number || task.order_number}</td>
                                                    <td className="px-4 py-3 text-sm font-mono">{task.location_code}</td>
                                                    <td className="px-4 py-3 text-sm">
                                                        <button
                                                            type="button"
                                                            onClick={() => toggleExpand(key)}
                                                            className="inline-flex items-center gap-1 font-mono text-xs text-gray-700 hover:text-blue-600 focus:outline-none"
                                                            disabled={products.length === 0}
                                                            title={i18n.t("picking_tasks.multi_picker.toggle_sku_detail")}
                                                        >
                                                            {isExpanded
                                                                ? <ChevronDownIcon className="h-3 w-3 text-gray-400" />
                                                                : <ChevronRightIcon className="h-3 w-3 text-gray-400" />}
                                                            <span>{primarySku}</span>
                                                            {extraSkus > 0 && (
                                                                <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 text-[10px] font-sans">
                                                                    +{extraSkus}
                                                                </span>
                                                            )}
                                                        </button>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${PICK_TYPE_STYLES[task.pick_type] || "bg-gray-100 text-gray-800"}`}>
                                                            {i18n.t(`picking_tasks.pick_types.${task.pick_type}`)}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-600">{task.expected_quantity}</td>
                                                    <td className="px-4 py-3">
                                                        <select
                                                            value={assignments[key] || ""}
                                                            onChange={(e) => handleChangePicker(key, e.target.value)}
                                                            className="border border-gray-300 rounded pl-2 pr-8 py-1 text-sm min-w-[160px]"
                                                            disabled={busy}
                                                        >
                                                            <option value="">{i18n.t("picking_tasks.multi_picker.unassigned")}</option>
                                                            {activePickers.map((picker) => (
                                                                <option key={picker.id} value={picker.id}>
                                                                    {picker.first_name} {picker.last_name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </td>
                                                </tr>
                                                {isExpanded && (
                                                    <tr className="bg-gray-50">
                                                        <td colSpan={7} className="px-12 py-2 border-t border-gray-100">
                                                            <div className="text-xs text-gray-600">
                                                                <div className="grid grid-cols-12 gap-2 font-medium text-gray-500 uppercase tracking-wide text-[10px] pb-1 border-b border-gray-200">
                                                                    <div className="col-span-3">{i18n.t("picking_tasks.columns.sku")}</div>
                                                                    <div className="col-span-3">{i18n.t("picking_tasks.multi_picker.upc")}</div>
                                                                    <div className="col-span-5">{i18n.t("picking_tasks.multi_picker.product_name")}</div>
                                                                    <div className="col-span-1 text-right">{i18n.t("picking_tasks.multi_picker.pieces_short")}</div>
                                                                </div>
                                                                {products.length === 0 ? (
                                                                    <div className="py-2 text-gray-400 italic">{i18n.t("picking_tasks.multi_picker.no_product_detail")}</div>
                                                                ) : products.map((p, idx) => (
                                                                    <div key={`${p.product_id}-${idx}`} className="grid grid-cols-12 gap-2 py-1.5 items-center">
                                                                        <div className="col-span-3 font-mono">{(p.skus || []).join(", ") || "—"}</div>
                                                                        <div className="col-span-3 font-mono text-gray-500">{p.upc || "—"}</div>
                                                                        <div className="col-span-5 text-gray-700">{p.name || "—"}</div>
                                                                        <div className="col-span-1 text-right text-gray-600">{p.expected_quantity}</div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </Fragment>
                                        )
                                    })}
                                </tbody>
                            </table>
                        </>
                    )}

                    {suggestedTasks.length > 0 && viewMode === "picker" && (
                        <div className="space-y-3">
                            {activePickers.map((picker) => {
                                const d = distribution.byPicker.get(picker.id) || { tasks: [], pieces: 0, pickTypes: {} }
                                const pickerOpen = openPickers.has(picker.id)
                                return (
                                    <div
                                        key={picker.id}
                                        onDragOver={(e) => { if (draggedTaskKeys) { e.preventDefault(); setDragOverPickerId(picker.id) } }}
                                        onDragLeave={(e) => { if (e.currentTarget === e.target) setDragOverPickerId(null) }}
                                        onDrop={(e) => { e.preventDefault(); reassignTasksTo(draggedTaskKeys, picker.id); setDraggedTaskKeys(null); setDragOverPickerId(null) }}
                                        className={`border rounded-lg overflow-hidden ${dragOverPickerId === picker.id ? "border-blue-400 ring-2 ring-blue-200" : "border-gray-200"}`}
                                    >
                                        <div className="flex items-center justify-between gap-2 px-4 py-3 bg-gray-50">
                                            <button
                                                type="button"
                                                onClick={() => toggleInSet(setOpenPickers, picker.id)}
                                                className="flex items-center gap-2 min-w-0 flex-1 text-left"
                                            >
                                                {pickerOpen
                                                    ? <ChevronDownIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                                    : <ChevronRightIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />}
                                                <span className="font-medium text-gray-800 truncate">{picker.first_name} {picker.last_name}</span>
                                            </button>
                                            <span className="flex items-center gap-3 flex-shrink-0">
                                                <span className="text-sm text-gray-500 whitespace-nowrap">
                                                    {i18n.t("picking_tasks.multi_picker.picker_summary", { locations: d.tasks.length, pieces: d.pieces, skus: d.skus.size, orders: d.orders.size })}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        if (d.tasks.length > 0) reassignTasksTo(d.tasks.map(taskKey), null)
                                                        setRemovedPickerIds(prev => new Set(prev).add(picker.id))
                                                    }}
                                                    title={i18n.t("picking_tasks.multi_picker.remove_picker")}
                                                    className="text-gray-400 hover:text-red-500"
                                                >
                                                    <XIcon className="h-4 w-4" />
                                                </button>
                                            </span>
                                        </div>
                                        {pickerOpen && (
                                            d.tasks.length === 0 ? (
                                                <div className="px-4 py-3 text-sm text-gray-400 italic">{i18n.t("picking_tasks.multi_picker.no_locations")}</div>
                                            ) : (
                                                <table className="min-w-full text-sm">
                                                    <thead className="bg-white">
                                                        <tr className="border-b border-gray-100">
                                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wide">{i18n.t("picking_tasks.columns.location")}</th>
                                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wide">{i18n.t("picking_tasks.multi_picker.pick_type_header")}</th>
                                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wide">Pedido</th>
                                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wide">SKU</th>
                                                            <th className="px-4 py-2 text-right text-xs font-medium text-gray-400 uppercase tracking-wide">{i18n.t("picking_tasks.assign_columns.pieces")}</th>
                                                            <th className="px-4 py-2"></th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-gray-100">
                                                        {d.tasks.map((task) => {
                                                            const k = taskKey(task)
                                                            const skus = (task.skus && task.skus.length > 0) ? task.skus.join(", ") : "—"
                                                            const splitColor = splitOrderColorById[task.order_id]
                                                            return (
                                                                <tr
                                                                    key={k}
                                                                    draggable
                                                                    onDragStart={() => setDraggedTaskKeys([k])}
                                                                    onDragEnd={() => { setDraggedTaskKeys(null); setDragOverPickerId(null) }}
                                                                    title={i18n.t("picking_tasks.multi_picker.drag_to_reassign")}
                                                                    className="cursor-move hover:bg-gray-50 select-none"
                                                                >
                                                                    <td className="px-4 py-2">
                                                                        <span className="flex items-center gap-2">
                                                                            <MenuIcon className="h-4 w-4 text-gray-300 flex-shrink-0" />
                                                                            <Copyable value={task.location_code} className="font-mono text-gray-700" />
                                                                        </span>
                                                                    </td>
                                                                    <td className="px-4 py-2">
                                                                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${PICK_TYPE_STYLES[task.pick_type] || "bg-gray-100 text-gray-800"}`}>
                                                                            {pickTypeLabel(task.pick_type)}
                                                                        </span>
                                                                    </td>
                                                                    <td className="px-4 py-2">
                                                                        {splitColor
                                                                            ? <Copyable value={task.order_number}><span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${splitColor}`}>#{task.order_number}</span></Copyable>
                                                                            : <Copyable value={task.order_number} className="text-gray-700">#{task.order_number}</Copyable>}
                                                                    </td>
                                                                    <td className="px-4 py-2"><Copyable value={skus} className="font-mono text-gray-700" /></td>
                                                                    <td className="px-4 py-2 text-right text-gray-600">{task.expected_quantity}</td>
                                                                    <td className="px-4 py-2 text-right">
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => reassignTasksTo([k], null)}
                                                                            title={i18n.t("picking_tasks.multi_picker.unassign_location")}
                                                                            className="text-gray-400 hover:text-red-500"
                                                                        >
                                                                            <XIcon className="h-4 w-4 inline" />
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                            )
                                                        })}
                                                    </tbody>
                                                </table>
                                            )
                                        )}
                                    </div>
                                )
                            })}
                            {distribution.unassigned.tasks.length > 0 && (
                                <div
                                    onDragOver={(e) => { if (draggedTaskKeys) { e.preventDefault(); setDragOverPickerId("__pending__") } }}
                                    onDragLeave={(e) => { if (e.currentTarget === e.target) setDragOverPickerId(null) }}
                                    onDrop={(e) => { e.preventDefault(); reassignTasksTo(draggedTaskKeys, null); setDraggedTaskKeys(null); setDragOverPickerId(null) }}
                                    className={`border rounded-lg overflow-hidden bg-yellow-50 ${dragOverPickerId === "__pending__" ? "border-yellow-500 ring-2 ring-yellow-200" : "border-yellow-300"}`}
                                >
                                    <div className="px-4 py-3 flex items-center justify-between gap-2">
                                        <span>
                                            <span className="font-medium text-yellow-800">{i18n.t("picking_tasks.multi_picker.unassigned")}</span>
                                            <span className="text-sm text-yellow-700 ml-2">{i18n.t("picking_tasks.multi_picker.locations_pieces", { locations: distribution.unassigned.tasks.length, pieces: distribution.unassigned.pieces })}</span>
                                        </span>
                                        {activePickers.length > 0 && (
                                            <button
                                                type="button"
                                                onClick={autoAssignPending}
                                                className="text-blue-600 font-medium hover:text-blue-700 text-sm whitespace-nowrap"
                                            >
                                                {i18n.t("picking_tasks.multi_picker.auto_assign")}
                                            </button>
                                        )}
                                    </div>
                                    <div className="divide-y divide-yellow-200">
                                        {distribution.unassigned.tasks.map((task) => {
                                            const k = taskKey(task)
                                            return (
                                                <div
                                                    key={k}
                                                    draggable
                                                    onDragStart={() => setDraggedTaskKeys([k])}
                                                    onDragEnd={() => { setDraggedTaskKeys(null); setDragOverPickerId(null) }}
                                                    title={i18n.t("picking_tasks.multi_picker.drag_to_assign")}
                                                    className="flex items-center justify-between gap-2 px-4 py-2 cursor-move hover:bg-yellow-100 select-none"
                                                >
                                                    <span className="flex items-center gap-2 min-w-0">
                                                        <MenuIcon className="h-4 w-4 text-yellow-500 flex-shrink-0" />
                                                        <Copyable value={task.location_code} className="font-mono text-sm text-gray-700" />
                                                        <Copyable value={task.order_number} className="text-xs text-gray-500">#{task.order_number}</Copyable>
                                                    </span>
                                                    <span className="text-sm text-gray-500 whitespace-nowrap">{task.expected_quantity} {i18n.t("picking_tasks.multi_picker.pieces_short")}</span>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {suggestedTasks.length > 0 && viewMode === "order" && (
                        <div className="space-y-3">
                            {orderGroups.map((o) => {
                                const pickerEntries = Array.from(o.byPicker.values())
                                const assignedPickers = pickerEntries.filter(e => !e.unassigned).length
                                return (
                                    <div key={o.order_id} className="border border-gray-200 rounded-lg overflow-hidden">
                                        <div className="flex items-center justify-between gap-2 px-4 py-3 bg-gray-50">
                                            <span className="min-w-0">
                                                <span className="font-medium text-gray-800">#{o.order_number}</span>
                                                {o.store?.name && <span className="text-sm text-gray-500 ml-2">{o.store.name}</span>}
                                            </span>
                                            <span className="text-sm text-gray-500 whitespace-nowrap">{i18n.t("picking_tasks.multi_picker.locations_pieces", { locations: o.tasks, pieces: o.pieces })}</span>
                                        </div>
                                        <div className="divide-y divide-gray-100">
                                            {pickerEntries.map((e, idx) => (
                                                <div key={idx} className="flex items-center justify-between gap-2 px-4 py-2 text-sm">
                                                    <span className={e.unassigned ? "text-yellow-700 font-medium" : "text-gray-700"}>{e.name}</span>
                                                    <span className="text-gray-500 whitespace-nowrap">{i18n.t("picking_tasks.multi_picker.locations_pieces_short", { locations: e.tasks, pieces: e.pieces })}</span>
                                                </div>
                                            ))}
                                        </div>
                                        {assignedPickers > 1 && (
                                            <div className="px-4 py-2 text-xs text-blue-700 bg-blue-50 border-t border-blue-100">
                                                {i18n.t("picking_tasks.multi_picker.order_split_notice", { n: assignedPickers })}
                                            </div>
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                    )}
                    </div>

                    <PickingOrderErrorsPanel errors={confirmErrors} className="flex-shrink-0 mt-3" />

                    <div className="flex-shrink-0 mt-4 flex justify-between border-t border-gray-100 pt-3">
                        <Button type="secondary" onClick={handleCancel} disabled={busy}>{i18n.t("picking_tasks.assign_dialog.back")}</Button>
                        <div className="flex gap-2">
                            <Button type="secondary" onClick={handleRebalance} disabled={busy || suggestedTasks.length === 0 || !hasEdits}>
                                {i18n.t("picking_tasks.multi_picker.restore_distribution")}
                            </Button>
                            <Button onClick={handleConfirm} disabled={busy || suggestedTasks.length === 0 || unassignedCount > 0}>
                                {i18n.t("picking_tasks.multi_picker.confirm")}
                            </Button>
                        </div>
                    </div>
            <Notification
                show={!!notification}
                setShow={() => setNotification(null)}
                type={notification?.type || "success"}
                title={notification?.message || ""}
            />
        </>
    )
}
